import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { fmtDate } from "@/lib/constants";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", customer_org: "", description: "" });

  const load = async () => {
    const r = await api.get("/projects");
    setProjects(r.data);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success("Projekt angelegt");
      setOpen(false);
      setForm({ name: "", customer_org: "", description: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    }
  };

  return (
    <InternalLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Index</div>
          <h1 className="font-display text-3xl md:text-4xl text-neutral-900">Projekte</h1>
          <p className="text-sm text-neutral-600 mt-2">Alle Projektakten in einer Übersicht.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-project-button"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-sm hover:opacity-95"
          style={{ background: "#183153" }}
        >
          <Plus className="w-4 h-4" /> Neues Projekt
        </button>
      </div>

      <div className="bg-white border border-neutral-200" data-testid="projects-list">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</th>
              <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Kunde</th>
              <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Phase intern</th>
              <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Phase Kunde</th>
              <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Erstellt</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-neutral-500 text-sm">Noch keine Projekte angelegt.</td></tr>
            )}
            {projects.map((p) => (
              <tr key={p.project_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-5 py-3">
                  <Link to={`/projects/${p.project_id}`} className="font-medium text-neutral-900 hover:text-[#183153]" data-testid={`project-link-${p.project_id}`}>
                    {p.name}
                  </Link>
                  <div className="font-mono text-[10px] text-neutral-500 mt-0.5">{p.project_id}</div>
                </td>
                <td className="px-5 py-3 text-neutral-700">{p.customer_org}</td>
                <td className="px-5 py-3"><StatusPill kind="phase" value={p.current_phase} /></td>
                <td className="px-5 py-3"><StatusPill kind="customer_phase" value={p.customer_phase} /></td>
                <td className="px-5 py-3 font-mono text-xs text-neutral-500">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-neutral-200 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="new-project-modal">
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
              <div className="font-display-medium text-lg">Neues Projekt</div>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={onCreate} className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="project-name-input"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153] text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Kunde / Organisation</label>
                <input
                  required
                  value={form.customer_org}
                  onChange={(e) => setForm({ ...form, customer_org: e.target.value })}
                  data-testid="project-customer-input"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153] text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Beschreibung</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="project-description-input"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#183153] text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-50 rounded-sm">Abbrechen</button>
                <button type="submit" data-testid="project-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Anlegen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </InternalLayout>
  );
}
